import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare, GitBranch, Paperclip, Edit, Plus, Clock } from 'lucide-react';
import { formatRelative } from '@/lib/format';
import { TICKET_STATUS_LABEL } from '@/lib/labels';
import { sanitizeHtml } from '@/lib/sanitize';
import { toast } from 'sonner';
import api from '@/lib/api';

// Mapeamento de ações para ícones e cores
const ACTION_CONFIG = {
  TICKET_CREATED: { icon: Plus, color: 'bg-green-100 text-green-700', label: 'Ticket criado' },
  TICKET_UPDATED: { icon: Edit, color: 'bg-blue-100 text-blue-700', label: 'Ticket atualizado' },
  COMMENT_ADDED: { icon: MessageSquare, color: 'bg-purple-100 text-purple-700', label: 'Comentário adicionado' },
  ATTACHMENT_ADDED: { icon: Paperclip, color: 'bg-amber-100 text-amber-700', label: 'Anexo adicionado' },
  status_change: { icon: GitBranch, color: 'bg-indigo-100 text-indigo-700', label: 'Status alterado' },
  default: { icon: Clock, color: 'bg-slate-100 text-slate-700', label: 'Evento' },
};

export default function HistoryTab({ ticketId, history, onRefresh }) {
  const [comment, setComment] = useState('');

  const submitComment = async () => {
    const value = comment.trim();
    if (!value) return;
    try {
      await api.post(`/tickets/${ticketId}/comments`, { comment: value });
      toast.success('Comentário adicionado');
      setComment('');
      onRefresh && onRefresh();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to add comment:', err);
      toast.error('Erro ao adicionar comentário');
    }
  };

  return (
    <Card className="border-slate-200 p-5 space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="Adicionar comentário ou nota..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submitComment(); }}
          aria-label="Comentário"
        />
        <Button onClick={submitComment} className="bg-blue-600 hover:bg-blue-700">
          <MessageSquare className="w-4 h-4 mr-2" /> Comentar
        </Button>
      </div>
      <div>
        {!history || history.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">Nenhum evento</p>
        ) : (
          <ul className="space-y-3">
            {history.map((h) => {
              const config = ACTION_CONFIG[h.type] || ACTION_CONFIG[h.action] || ACTION_CONFIG.default;
              const Icon = config.icon;
              
              return (
                <li key={h.id} className="flex gap-3 pb-3 border-b border-slate-100 last:border-0">
                  <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm text-slate-900">
                        <span className="font-medium">{h.userName || 'Sistema'}</span>
                        <span className="text-slate-500"> • {h.action || config.label}</span>
                      </div>
                      <div className="text-xs text-slate-400 whitespace-nowrap">{formatRelative(h.createdAt)}</div>
                    </div>
                    
                    {h.description && h.action !== 'COMMENT_ADDED' && (
                      <div 
                        className="text-sm text-slate-700 mt-1 break-words"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(h.description) }}
                      />
                    )}
                    
                    {h.type === 'audit' && h.newValue && (
                      <div className="mt-2 text-sm text-slate-600">
                        {h.action === 'COMMENT_ADDED' && h.newValue.comment ? (
                          <div className="italic text-slate-700">"{h.newValue.comment}"</div>
                        ) : h.action === 'TICKET_UPDATED' && typeof h.newValue === 'object' ? (
                          <div className="space-y-1">
                            {Object.entries(h.newValue).map(([key, value]) => {
                              if (key === 'status') return (
                                <div key={key}>
                                  Status: <span className="font-medium">{TICKET_STATUS_LABEL[value] || value}</span>
                                </div>
                              );
                              if (key === 'priority') return (
                                <div key={key}>
                                  Prioridade: <span className="font-medium">{value}</span>
                                </div>
                              );
                              return null;
                            })}
                          </div>
                        ) : null}
                      </div>
                    )}
                    
                    {h.type === 'status_change' && (
                      <div className="mt-1 text-xs">
                        <span className="text-slate-500">
                          {h.previousStatus ? TICKET_STATUS_LABEL[h.previousStatus] || h.previousStatus : 'N/A'}
                        </span>
                        <span className="mx-1 text-slate-400">→</span>
                        <span className="font-medium text-slate-700">
                          {TICKET_STATUS_LABEL[h.newStatus] || h.newStatus}
                        </span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
